// A top-down reference picture overlaid on the Model-mode canvas: a flat,
// semi-transparent plane you can translate / rotate / scale in the view to align
// with the work volume, then carve the voxel block to match. Lives under
// gridGroup so it shares the voxel coordinate frame. Session-only (never saved).
//
// All transform math is done in gridGroup-LOCAL space: the owner projects the
// cursor into that frame and hands this class plain (x,z) points. The plane lies
// in local XZ at a fixed y (the volume top); yaw is about Y; scale is uniform.

import * as THREE from "three";
import type { ReferenceData } from "../model/saved-model";

export type RefHandle = "translate" | "scale" | "rotate";

const HANDLE_R = 0.07; // world radius of the corner/rotate handles (metres)
const ROTATE_MARGIN = 0.35; // how far the rotate handle sits beyond the top edge
const MIN_SCALE = 0.05;

const _v = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _basis = new THREE.Matrix4();
const _frameInv = new THREE.Matrix4();
const _localRay = new THREE.Ray();
const _projPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const _projHit = new THREE.Vector3();

export interface VolumeBox {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  height: number;
}

export class ReferenceImage {
  // `frame` (child of gridGroup) holds the view-face ORIENTATION — its local XZ
  // plane faces whatever side you imported from. `group` (child of frame) carries
  // the user transform (in-plane translate / yaw / scale); all the drag + handle
  // math runs in frame-local space, so it's identical to the old top-down case.
  readonly frame = new THREE.Group();
  readonly group = new THREE.Group();
  private mesh: THREE.Mesh | null = null;
  private mat: THREE.MeshBasicMaterial | null = null;
  private tex: THREE.Texture | null = null;
  private baseW = 1; // plane geometry size (encodes the image aspect + base fit)
  private baseH = 1;
  private opacity = 0.5;
  private src = ""; // the image's data URL, for saving with the model

  private handleGroup = new THREE.Group();
  private handleGeo = new THREE.SphereGeometry(HANDLE_R, 12, 8);
  // transparent + a very high renderOrder so the grips draw in the transparent
  // pass AFTER (and over) the work-volume walls/surfaces — never occluded.
  private cornerMat = new THREE.MeshBasicMaterial({
    color: 0x6fa0ff,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  private rotateMat = new THREE.MeshBasicMaterial({
    color: 0xffd24a,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  private handles: THREE.Mesh[] = []; // 0..3 corners, 4 = rotate

  // Active drag, captured at grab so the transform doesn't jump.
  private drag:
    | { kind: RefHandle; gx: number; gz: number; cx: number; cz: number; yaw: number; s: number; gd: number; ga: number }
    | null = null;

  constructor() {
    this.frame.add(this.group);
    this.group.visible = false;
    this.handleGroup.visible = false;
    this.group.add(this.handleGroup);
    for (let i = 0; i < 5; i++) {
      const m = new THREE.Mesh(this.handleGeo, i === 4 ? this.rotateMat : this.cornerMat);
      m.renderOrder = 9001; // above the work-volume surfaces (≤951)
      this.handles.push(m);
      this.handleGroup.add(m);
    }
  }

  hasImage(): boolean {
    return this.mesh !== null;
  }
  localY(): number {
    return this.group.position.y;
  }

  // Build the plane mesh from an image using the current baseW/baseH + opacity.
  private build(img: HTMLImageElement): void {
    this.tex = new THREE.Texture(img);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.tex.needsUpdate = true;
    this.mat = new THREE.MeshBasicMaterial({
      map: this.tex,
      transparent: true,
      opacity: this.opacity,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(this.baseW, this.baseH), this.mat);
    this.mesh.rotation.x = -Math.PI / 2; // lie flat in XZ, facing up
    this.mesh.renderOrder = 9000; // over the work-volume surfaces, under the grips
    this.group.add(this.mesh);
    this.group.visible = true;
  }

  // Import a fresh image onto the face whose outward normal is `normal` (one of
  // ±X/±Y/±Z — the side you're looking at). Fit to that face's in-plane extents
  // and centre on it. `src` is the image's data URL, kept so it can be saved.
  load(img: HTMLImageElement, vol: VolumeBox, src: string, normal: THREE.Vector3): void {
    this.clear();
    this.src = src;
    const aspect = img.width / Math.max(1, img.height);
    const ex = vol.maxX - vol.minX;
    const ey = vol.height;
    const ez = vol.maxZ - vol.minZ;
    const inPlaneMax =
      Math.abs(normal.y) > 0.5
        ? Math.max(ex, ez) // top/bottom face: X × Z
        : Math.abs(normal.z) > 0.5
          ? Math.max(ex, ey) // front/back face: X × Y
          : Math.max(ez, ey); // side face: Z × Y
    const volMax = Math.max(inPlaneMax, 0.1);
    if (aspect >= 1) {
      this.baseW = volMax;
      this.baseH = volMax / aspect;
    } else {
      this.baseH = volMax;
      this.baseW = volMax * aspect;
    }
    this.build(img);
    this.orientFrame(normal, vol);
    this.group.position.set(0, 0.02, 0); // just outside the face, along its normal
    this.group.rotation.set(0, 0, 0);
    this.group.scale.setScalar(1);
    this.layoutHandles();
  }

  // Orient + position `frame` so its local XZ plane faces `n` (the side toward the
  // camera), upright, sitting on the volume's matching face.
  private orientFrame(n: THREE.Vector3, vol: VolumeBox): void {
    // local +Y (plane normal) → n; local -Z (the image's "up") → world up.
    const up =
      Math.abs(n.y) > 0.5
        ? new THREE.Vector3(0, 0, n.y > 0 ? -1 : 1) // top/bottom: ∓Z up so +X reads right
        : new THREE.Vector3(0, 1, 0); // sides: world up
    const yCol = n.clone().normalize();
    const zCol = up.negate(); // local +Z = -up
    const xCol = new THREE.Vector3().crossVectors(yCol, zCol).normalize();
    _basis.makeBasis(xCol, yCol, zCol);
    this.frame.quaternion.setFromRotationMatrix(_basis);
    this.placeOnFarFace(n, vol);
  }

  // Position the frame on the face OPPOSITE the facing normal `n` — the far wall,
  // not the one closest to the camera (the image still faces toward you).
  private placeOnFarFace(n: THREE.Vector3, vol: VolumeBox): void {
    this.frame.position.set(
      n.x > 0.5 ? vol.minX : n.x < -0.5 ? vol.maxX : (vol.minX + vol.maxX) / 2,
      n.y > 0.5 ? 0 : n.y < -0.5 ? vol.height : vol.height / 2,
      n.z > 0.5 ? vol.minZ : n.z < -0.5 ? vol.maxZ : (vol.minZ + vol.maxZ) / 2,
    );
  }

  // Re-position the frame onto the matching face of a resized volume (keeping its
  // orientation + the user's in-plane transform). The work volume's metric
  // extents change with the voxel density, so without this the image is left
  // floating inside or outside the canvas after a resize.
  reanchor(vol: VolumeBox): void {
    if (!this.mesh) return;
    const n = _v.set(0, 1, 0).applyQuaternion(this.frame.quaternion); // plane normal (world)
    this.placeOnFarFace(n, vol);
  }

  // Project a gridGroup-LOCAL ray onto the image plane, returning FRAME-local
  // (x,z) — the coordinates pickHandle/applyDrag expect. Null if it misses.
  projectRay(ray: THREE.Ray): { x: number; z: number } | null {
    this.frame.updateMatrix();
    _frameInv.copy(this.frame.matrix).invert();
    _localRay.copy(ray).applyMatrix4(_frameInv); // gridGroup-local → frame-local
    _projPlane.constant = -this.group.position.y;
    return _localRay.intersectPlane(_projPlane, _projHit)
      ? { x: _projHit.x, z: _projHit.z }
      : null;
  }

  // Restore a saved reference exactly (size + transform), no re-fitting.
  loadSaved(data: ReferenceData, img: HTMLImageElement): void {
    this.clear();
    this.src = data.src;
    this.baseW = data.baseW;
    this.baseH = data.baseH;
    this.opacity = data.opacity;
    this.build(img);
    if (data.framePos && data.frameQuat) {
      this.frame.position.fromArray(data.framePos);
      this.frame.quaternion.fromArray(data.frameQuat);
    } else {
      // Legacy top-down data: frame is identity (the group held the world transform).
      this.frame.position.set(0, 0, 0);
      this.frame.quaternion.identity();
    }
    this.group.position.fromArray(data.p);
    this.group.quaternion.fromArray(data.q);
    this.group.scale.setScalar(data.s);
    this.layoutHandles();
  }

  // Snapshot for saving with the model (null if no image loaded).
  serialize(): ReferenceData | null {
    if (!this.mesh) return null;
    return {
      src: this.src,
      baseW: this.baseW,
      baseH: this.baseH,
      p: this.group.position.toArray(),
      q: this.group.quaternion.toArray(),
      s: this.group.scale.x,
      opacity: this.opacity,
      framePos: this.frame.position.toArray(),
      frameQuat: this.frame.quaternion.toArray(),
    };
  }

  clear(): void {
    if (this.mesh) {
      this.group.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh = null;
    }
    this.mat?.dispose();
    this.mat = null;
    this.tex?.dispose();
    this.tex = null;
    this.drag = null;
    this.group.visible = false;
    this.handleGroup.visible = false;
  }

  setOpacity(v: number): void {
    this.opacity = Math.max(0, Math.min(1, v));
    if (this.mat) this.mat.opacity = this.opacity;
  }
  getOpacity(): number {
    return this.opacity;
  }

  setEditMode(on: boolean): void {
    this.handleGroup.visible = on && this.hasImage();
  }

  // Which handle (if any) is under a gridGroup-local point. Uses the group's
  // ACTUAL matrix so it always matches the visible handles (no hand-rolled
  // rotation that could disagree with THREE's). Corners → scale, the offset dot
  // → rotate, inside the rect → translate.
  pickHandle(x: number, z: number): RefHandle | null {
    if (!this.hasImage()) return null;
    const rot = this.rotatePoint();
    if (Math.hypot(x - rot.x, z - rot.z) <= HANDLE_R * 2.2) return "rotate";
    for (const c of this.cornerPoints()) {
      if (Math.hypot(x - c.x, z - c.z) <= HANDLE_R * 2.2) return "scale";
    }
    this.group.updateMatrix();
    _m.copy(this.group.matrix).invert();
    _v.set(x, 0, z).applyMatrix4(_m); // gridGroup-local → group-local (geometry units)
    return Math.abs(_v.x) <= this.baseW / 2 && Math.abs(_v.z) <= this.baseH / 2
      ? "translate"
      : null;
  }

  beginDrag(kind: RefHandle, x: number, z: number): void {
    const cx = this.group.position.x;
    const cz = this.group.position.z;
    this.drag = {
      kind,
      gx: x,
      gz: z,
      cx,
      cz,
      yaw: this.group.rotation.y,
      s: this.group.scale.x,
      gd: Math.hypot(x - cx, z - cz) || 1,
      ga: Math.atan2(z - cz, x - cx),
    };
  }

  // snapAngle: rotate snaps to 15° steps (false = free / Shift held).
  applyDrag(x: number, z: number, snapAngle: boolean): void {
    const d = this.drag;
    if (!d) return;
    if (d.kind === "translate") {
      this.group.position.x = d.cx + (x - d.gx);
      this.group.position.z = d.cz + (z - d.gz);
    } else if (d.kind === "scale") {
      const dist = Math.hypot(x - d.cx, z - d.cz);
      this.group.scale.setScalar(Math.max(MIN_SCALE, d.s * (dist / d.gd)));
    } else {
      // +Y rotation is clockwise in the XZ screen-plane (top-down), so negate
      // the cursor-angle delta to turn the way the cursor moves.
      const a = Math.atan2(z - d.cz, x - d.cx);
      let yaw = d.yaw - (a - d.ga);
      if (snapAngle) {
        const step = Math.PI / 12; // 15°
        yaw = Math.round(yaw / step) * step;
      }
      this.group.rotation.y = yaw;
    }
    this.layoutHandles();
  }

  endDrag(): void {
    this.drag = null;
  }

  // Transform snapshot (for the reference-image undo stack).
  transformJSON(): string {
    return JSON.stringify({
      p: this.group.position.toArray(),
      q: this.group.quaternion.toArray(),
      s: this.group.scale.x,
    });
  }
  applyTransformJSON(json: string): void {
    const t = JSON.parse(json) as { p: number[]; q: number[]; s: number };
    this.group.position.fromArray(t.p);
    this.group.quaternion.fromArray(t.q);
    this.group.scale.setScalar(t.s);
    this.layoutHandles();
  }

  // Corner / rotate positions (gridGroup-local) via the group's actual matrix,
  // so they line up with the visible handles at any rotation/scale.
  private cornerPoints(): { x: number; z: number }[] {
    this.group.updateMatrix();
    const hw = this.baseW / 2;
    const hh = this.baseH / 2;
    return [
      [-hw, -hh],
      [hw, -hh],
      [hw, hh],
      [-hw, hh],
    ].map(([lx, lz]) => {
      _v.set(lx, 0, lz).applyMatrix4(this.group.matrix);
      return { x: _v.x, z: _v.z };
    });
  }
  private rotatePoint(): { x: number; z: number } {
    this.group.updateMatrix();
    _v.set(0, 0, -(this.baseH / 2 + ROTATE_MARGIN)).applyMatrix4(this.group.matrix);
    return { x: _v.x, z: _v.z };
  }

  // Place the handle markers directly at the geometry-space corners + rotate
  // point (children of group, so its transform carries them — exactly matching
  // cornerPoints/rotatePoint); counter-scale so they stay a constant size.
  private layoutHandles(): void {
    if (!this.hasImage()) return;
    const y = 0.03;
    const hw = this.baseW / 2;
    const hh = this.baseH / 2;
    const s = this.group.scale.x || 1;
    const pts = [
      [-hw, -hh],
      [hw, -hh],
      [hw, hh],
      [-hw, hh],
    ];
    pts.forEach(([lx, lz], i) => {
      this.handles[i].position.set(lx, y, lz);
      this.handles[i].scale.setScalar(1 / s);
    });
    this.handles[4].position.set(0, y, -(hh + ROTATE_MARGIN));
    this.handles[4].scale.setScalar(1 / s);
  }
}
